/**
 * MJPEG Frame Hub — maintains ONE hidden <img> per camera URL.
 *
 * webSecurity: false in Electron's BrowserWindow disables the cross-origin
 * canvas restriction, so canvas.toDataURL() works for images loaded from
 * local-network cameras (192.168.x.x) without CORS headers.
 *
 * One <img> → one HTTP connection per camera URL, regardless of how many
 * React components subscribe (e.g. MONITORAMENTO + ROSTO in different modals
 * can show the same camera with zero conflict).
 */

export type MjpegStatus = 'connecting' | 'live' | 'error';

type FrameCallback = (dataUrl: string | null, status: MjpegStatus) => void;

interface StreamEntry {
  img: HTMLImageElement;
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D | null;
  status: MjpegStatus;
  lastFrame: string | null;
  subscribers: Map<string, FrameCallback>;
  frameTimer: ReturnType<typeof setInterval> | null;
  connectTimer: ReturnType<typeof setTimeout> | null;
  pollTimer: ReturnType<typeof setInterval> | null;
  mountDiv: HTMLDivElement;
}

class MjpegStreamRegistry {
  private streams = new Map<string, StreamEntry>();

  /**
   * Subscribe to a camera URL.
   * Returns a cleanup function that unsubscribes and destroys the stream
   * if this was the last subscriber.
   */
  subscribe(url: string, id: string, cb: FrameCallback): () => void {
    if (!this.streams.has(url)) {
      this.createStream(url);
    }
    const entry = this.streams.get(url)!;
    entry.subscribers.set(id, cb);
    // Deliver current state immediately (important when resubscribing to live stream)
    cb(entry.lastFrame, entry.status);

    return () => {
      const e = this.streams.get(url);
      if (!e) return;
      e.subscribers.delete(id);
      if (e.subscribers.size === 0) {
        this.destroyStream(url);
      }
    };
  }

  /** Get the most recent captured frame data URL (used for snapshot capture). */
  getLastFrame(url: string): string | null {
    return this.streams.get(url)?.lastFrame ?? null;
  }

  /** Force reconnect all subscribers of a URL. */
  refresh(url: string) {
    const entry = this.streams.get(url);
    if (!entry) return;
    entry.lastFrame = null;
    this.restartStream(url, entry);
    entry.status = 'connecting';
    entry.subscribers.forEach(cb => cb(null, 'connecting'));
  }

  private createStream(url: string) {
    const mountDiv = document.createElement('div');
    mountDiv.style.cssText =
      'position:fixed;left:-9999px;top:-9999px;width:2px;height:2px;overflow:hidden;pointer-events:none;opacity:0;';
    document.body.appendChild(mountDiv);

    const img = document.createElement('img');
    // No crossOrigin attribute — webSecurity:false handles canvas access
    mountDiv.appendChild(img);

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    const entry: StreamEntry = {
      img, canvas, ctx,
      status: 'connecting',
      lastFrame: null,
      subscribers: new Map(),
      frameTimer: null,
      connectTimer: null,
      pollTimer: null,
      mountDiv,
    };

    this.streams.set(url, entry);
    this.restartStream(url, entry);
  }

  private restartStream(url: string, entry: StreamEntry) {
    if (entry.frameTimer)  { clearInterval(entry.frameTimer);  entry.frameTimer  = null; }
    if (entry.connectTimer){ clearTimeout(entry.connectTimer); entry.connectTimer = null; }
    if (entry.pollTimer)   { clearInterval(entry.pollTimer);   entry.pollTimer   = null; }

    entry.img.onerror = () => {
      if (entry.frameTimer)  { clearInterval(entry.frameTimer);  entry.frameTimer  = null; }
      if (entry.pollTimer)   { clearInterval(entry.pollTimer);   entry.pollTimer   = null; }
      if (entry.connectTimer){ clearTimeout(entry.connectTimer); entry.connectTimer = null; }
      entry.status = 'error';
      entry.subscribers.forEach(cb => cb(null, 'error'));
    };

    // Force reload
    entry.img.src = '';
    entry.img.src = url;

    // Poll naturalWidth — onLoad is unreliable for multipart/x-mixed-replace
    entry.pollTimer = setInterval(() => {
      if (entry.img.naturalWidth > 0 && entry.img.naturalHeight > 0) {
        if (entry.pollTimer)   { clearInterval(entry.pollTimer);   entry.pollTimer   = null; }
        if (entry.connectTimer){ clearTimeout(entry.connectTimer); entry.connectTimer = null; }
        entry.status = 'live';
        this.startFrameCapture(entry);
      }
    }, 100);

    // 15-second connection timeout
    entry.connectTimer = setTimeout(() => {
      if (entry.pollTimer) { clearInterval(entry.pollTimer); entry.pollTimer = null; }
      if (entry.status === 'connecting') {
        entry.status = 'error';
        entry.subscribers.forEach(cb => cb(null, 'error'));
      }
    }, 15_000);
  }

  private startFrameCapture(entry: StreamEntry) {
    entry.frameTimer = setInterval(() => {
      try {
        const { naturalWidth: w, naturalHeight: h } = entry.img;
        if (w === 0 || h === 0) return;
        if (entry.canvas.width  !== w) entry.canvas.width  = w;
        if (entry.canvas.height !== h) entry.canvas.height = h;
        entry.ctx?.drawImage(entry.img, 0, 0);
        const dataUrl = entry.canvas.toDataURL('image/jpeg', 0.85);
        if (dataUrl && dataUrl.length > 50) {
          entry.lastFrame = dataUrl;
          entry.subscribers.forEach(cb => cb(dataUrl, 'live'));
        }
      } catch {
        // SecurityError or drawing error — ignore silently
      }
    }, 67); // ~15 fps
  }

  private destroyStream(url: string) {
    const entry = this.streams.get(url);
    if (!entry) return;
    if (entry.frameTimer)   clearInterval(entry.frameTimer);
    if (entry.connectTimer) clearTimeout(entry.connectTimer);
    if (entry.pollTimer)    clearInterval(entry.pollTimer);
    entry.img.onerror = null;
    entry.img.src = '';
    entry.mountDiv.remove();
    this.streams.delete(url);
  }
}

export const mjpegRegistry = new MjpegStreamRegistry();
